import type { FormEvent } from 'react';
import { useRef, useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowDownward, Print } from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import {
  Checkbox,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Input,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  styled,
} from '@mui/material';
import { useAtom, useSetAtom } from 'jotai';
import { exportComponentAsJPEG } from 'react-component-export-image';
import { useSelector } from 'react-redux';
import { useReactToPrint } from 'react-to-print';

import { VideaButton } from '@/common-components/elements/VideaButton/VideaButton';
import { VideaTypography } from '@/common-components/elements/VideaTypography/VideaTypography';
import { modelsTogglesAtom } from '@/modules/annotator/findingSidebar/atoms';
import { blue100, blueBlack400, green400 } from '@/theme/videa-style-tokens';
import type { EventName } from '@/utils/mixpanel';
import { mixpanelEvents } from '@/utils/mixpanel';

import { useGetCurrentImage } from '../../multiFileViewer/findingViewer/imageBrowser/helpers';
import type { AdultTreatmentKey } from '../../patientEducationDialog/AdultTabs';
import {
  disableKeyPressAtom,
  patientDownloadableNotesAtom,
  patientDownloadableSelectedTreatmentsAtom,
} from '../atoms';

import type { DownloadOption } from './DownloadOptions';
import { FindingsCheckboxes } from './FindingsCheckboxes';
import { DownloadableStyles } from './PatientDownloadable';
import { PatientDownloadableLoader } from './PatientDownloadableLoader';
import { useAdultTreatmentOptions } from './TreatmentOptions';

interface DeprecatedPreviewDialogProps {
  isPreviewOpen: boolean;
  setIsPreviewOpen: (isPreviewOpen: boolean) => void;
  selectedPatientId: string;
  selectedOption: DownloadOption | undefined;
  handleTrackEvent: (eventName: EventName, mode: DownloadOption) => void;
}

const PreviewDialog = styled(Dialog)`
  display: flex;
  justify-content: center;
  align-items: center;
  .MuiDialog-paper {
    display: flex;
    height: fit-content;
    padding: 0;
    border-radius: 1em;
    background-color: ${blueBlack400};
  }
  .MuiDialogTitle-root {
    display: flex;
    height: fit-content;
    flex-direction: column;
    color: white;
    gap: 0.75em;
    .title {
      font-size: 0.675em;
    }
    .treatments {
      display: flex;
      flex-direction: row;
      width: 100%;
      .treatments-select {
        width: 50%;
        padding-bottom: 0.5em;
        display: flex;
        flex-wrap: wrap;
        gap: 0.75em;
        font-size: 0.75em;
        .label {
          width: 16em;
          color: white;
        }
        .select {
          flex-wrap: nowrap;
          width: 10em;
          height: 2.5em;
          background-color: white;
          font-size: 0.75em;
        }
      }
      .dialog-section {
        width: 50%;
        display: flex;
        flex-wrap: wrap;
        font-size: 0.55em;
        strong {
          font-weight: 500;
          font-size: 1.35em;
          display: block;
          width: 100%;
          margin-bottom: 0.75em;
        }
      }
      &.notes {
        width: 80%;
        display: flex;
        justify-content: space-between;
        font-size: 0.75em;
        align-items: center;
      }
    }
  }
  .MuiDialogContent-root {
    display: flex;
    height: fit-content;
    margin: 0 1em 0 1em;
    background-color: white;
  }
  .MuiDialogActions-root {
    display: flex;
    height: fit-content;
    padding: 0.5em 1em;
    gap: 0.5em;
    background-color: ${blueBlack400};
    justify-content: flex-end;
    .download-options {
      display: flex;
      gap: 0.5em;
      .download-button {
        height: 2.5em;
        width: 7.5em;
        background-color: ${green400};
      }
    }
  }
`;

const StyledInput = styled(Input)`
  background: white;
  font-size: 0.625em;
  padding: 1em;
  border-radius: 0.5em;
  width: 60em;
  border: 1px solid grey;
  &:hover {
    border-color: ${blue100};
  }
`;

export const DeprecatedPreviewDialog = ({
  setIsPreviewOpen,
  selectedPatientId,
  selectedOption,
  isPreviewOpen,
  handleTrackEvent,
}: DeprecatedPreviewDialogProps) => {
  const [showTreatmentDiagrams, setShowTreatmentDiagrams] = useState(true);
  const [notes, setNotes] = useAtom(patientDownloadableNotesAtom);
  const setDisableKeyPress = useSetAtom(disableKeyPressAtom);
  const [selectedTreatments, setSelectedTreatments] = useAtom(
    patientDownloadableSelectedTreatmentsAtom,
  );

  const [enabledFindingTypes, setEnabledFindingTypes] =
    useAtom(modelsTogglesAtom);

  const patients = useSelector((state: any) => state.annotator?.patients) || {};
  const currentPatient = patients[selectedPatientId] || {};
  const { t } = useLingui();

  const selectedImages = useGetCurrentImage();
  const currentImage = selectedImages?.currentImage;
  const selectedPhoto = selectedImages?.selectedPhoto;

  const componentRef = useRef<HTMLDivElement>(null);
  const handleDownload = (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!currentImage) return;

    const today = new Date().toLocaleDateString();

    exportComponentAsJPEG(componentRef, {
      fileName: currentPatient.name
        ? currentPatient.name.split(' ').concat(today).join('_')
        : `Case_Presentation_Download_${today}`,
    });
  };

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
  });

  const treatmentsOptions = useAdultTreatmentOptions();
  const treatmentsList = Object.entries(treatmentsOptions).map(
    ([key, value]) => {
      return { treatment: key as AdultTreatmentKey, name: value.label };
    },
  );

  const handleSelect = (e: SelectChangeEvent<string | string[]>) => {
    const treatmentsSelected =
      typeof e.target.value === 'string' ? [e.target.value] : e.target.value;
    setSelectedTreatments(
      treatmentsSelected.map((treatment) => treatment as AdultTreatmentKey),
    );
  };

  const handleChangeTreatmentDiagrams = () => {
    setShowTreatmentDiagrams(!showTreatmentDiagrams);
  };

  return (
    <PreviewDialog
      open={isPreviewOpen}
      onClose={() => {
        setIsPreviewOpen(false);
        setNotes('');
        setSelectedTreatments([]);
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        <VideaTypography className="title" variant="labelSmallStrong">
          <Trans>FINDINGS SUMMARY</Trans>
        </VideaTypography>
        <div className="treatments">
          <div className="treatments-select">
            <VideaTypography className="label" variant="strong">
              <Trans>
                Select a treatment to include a patient-language description
              </Trans>
            </VideaTypography>
            <FormControl>
              <Select
                onChange={handleSelect}
                multiple
                value={selectedTreatments}
                className="select"
                displayEmpty
                renderValue={(selected) =>
                  selected.length
                    ? selected
                        .map(
                          (selectedValue) =>
                            treatmentsList.find(
                              (tx) => tx.treatment === selectedValue,
                            )?.name,
                        )
                        .join(', ')
                    : t`None`
                }
              >
                {treatmentsList.map((treatment) => (
                  <MenuItem key={treatment.name} value={treatment.treatment}>
                    <Checkbox
                      checked={selectedTreatments.includes(treatment.treatment)}
                    />

                    <ListItemText primary={treatment.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!!selectedTreatments.length && (
              <FormControlLabel
                control={
                  <Switch
                    defaultChecked={showTreatmentDiagrams}
                    onChange={handleChangeTreatmentDiagrams}
                    value={showTreatmentDiagrams}
                  />
                }
                label={t`Include diagrams with treatment descriptions`}
              />
            )}
          </div>
          <div className="dialog-section">
            <strong>
              <Trans>Select finding types to include in summary:</Trans>
            </strong>
            <FindingsCheckboxes
              enabledFindingTypes={enabledFindingTypes}
              setEnabledFindingTypes={setEnabledFindingTypes}
            />
          </div>
        </div>
        <div className="treatments notes">
          <VideaTypography className="label" variant="strong">
            <Trans>Notes (optional):</Trans>
          </VideaTypography>
          <ClickAwayListener onClickAway={() => setDisableKeyPress(false)}>
            <StyledInput
              multiline
              disableUnderline
              placeholder={t`Add Notes`}
              className="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              onClick={() => setDisableKeyPress(true)}
              fullWidth
            />
          </ClickAwayListener>
        </div>
      </DialogTitle>
      <DialogContent className="content">
        {currentImage && (
          <DownloadableStyles
            ref={componentRef as React.RefObject<HTMLDivElement>}
          >
            <PatientDownloadableLoader
              currentImage={currentImage}
              selectedPhoto={selectedPhoto}
            />
          </DownloadableStyles>
        )}
      </DialogContent>
      <DialogActions>
        {!currentImage && (
          <p className="vai-font-inter vai-text-sm vai-italic">
            <Trans>No images to </Trans>
            {selectedOption === 'print' ? t`print` : t`download`}
          </p>
        )}
        <div className="download-options">
          {selectedOption === 'print' ? (
            <VideaButton
              className="download-button"
              disabled={!currentImage}
              startIcon={<Print />}
              onClick={() => {
                handlePrint();
                handleTrackEvent(
                  mixpanelEvents.casePresentationSuggestion,
                  'print',
                );
              }}
            >
              <Trans>Print</Trans>
            </VideaButton>
          ) : (
            <VideaButton
              className="download-button"
              disabled={!currentImage}
              startIcon={<ArrowDownward />}
              onClick={(e: FormEvent<HTMLButtonElement>) => {
                handleDownload(e);
                handleTrackEvent(
                  mixpanelEvents.casePresentationSuggestion,
                  'download',
                );
              }}
            >
              <Trans>Download</Trans>
            </VideaButton>
          )}
        </div>
      </DialogActions>
    </PreviewDialog>
  );
};
